<template>
  <div v-loading="loading || quickSaving">
    <el-card>
      <div slot="header" class="task-card-header">
        <span>申诉单概况</span>
        <el-button
          v-if="isInit"
          type="primary"
          size="small"
          style="float:right"
          :loading="quickSaving"
          @click="handleQuickSave"
        >
          快速提单
        </el-button>
      </div>

      <el-alert v-if="!form.id" :title="tip" type="info" class="task-tip"></el-alert>

      <el-form
        ref="form"
        label-width="125px"
        :model="form"
        :rules="rules"
      >
        <el-row>
          <el-col v-if="form.id" :span="8">
            <el-form-item label="申诉单ID">{{ form.id }}</el-form-item>
          </el-col>
          <el-col v-if="canCheck" :span="8">
            <el-form-item label="高危类型评估">
              <el-select v-model="form.highType" class="form-field">
                <el-option
                  v-for="(item, key) in highTypeMap"
                  :key="key"
                  :value="+key"
                  :label="item"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="标题" prop="appealTitle">
              <el-input
                placeholder="请输入"
                v-model="form.appealTitle"
                clearable
                :disabled="!canSubmit"
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="申诉方类型" prop="appealType" required>
              <el-select v-model="form.appealType" :disabled="!isInit" class="form-field">
                <el-option
                  v-for="(item, key) in appealUserTypeMap"
                  :key="key"
                  :value="+key"
                  :label="item"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="申诉方名称" prop="appealName">
              <el-input
                placeholder="请输入"
                v-model="form.appealName"
                class="form-field"
                clearable
                :disabled="!canSubmit"
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="申诉方来源" required prop="appealFrom">
              <el-select v-model="form.appealFrom" :disabled="!canSubmit" class="form-field">
                <el-option
                  v-for="(item, key) in appealFromMap"
                  :key="key"
                  :value="+key"
                  :label="item"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="申诉类型" prop="applicationType" required>
              <el-select v-model="form.applicationType" class="form-field" :disabled="!isInit" @change="handleApplicationTypeChange">
                <el-option
                  v-for="(item, key) in appealTypeMap"
                  :key="key"
                  :value="+key"
                  :label="item"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="申诉一级场景" prop="bussType" required>
              <el-select v-model="form.bussType" class="form-field" :disabled="!isInit" @change="handleBussTypeChange">
                <el-option
                  v-for="(name, id) in levelOneAppealMap"
                  :key="+id"
                  :value="+id"
                  :label="name"
                  :disabled="disableBussType(+id)"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="申诉二级场景" prop="complaintType" required>
              <el-select v-model="form.complaintType" class="form-field" :disabled="cantEdit">
                <el-option
                  v-for="(item, key) in levelTwoAppealMap"
                  :key="key"
                  :value="+key"
                  :label="item"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="申诉发起时间" prop="applicationTime">
              <el-date-picker
                v-model="form.applicationTime"
                type="datetime"
                placeholder="请选择"
                class="form-field"
                value-format="yyyy-MM-dd"
                :disabled="!canSubmit"
              ></el-date-picker>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="申诉人" prop="applicationUser">
              <el-input
                placeholder="请输入"
                v-model="form.applicationUser"
                class="form-field"
                clearable
                :disabled="!canSubmit"
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="备注">
              <el-input
                v-model="form.remark"
                type="textarea"
                placeholder="请输入"
                :rows="3"
                :disabled="!canSubmit"
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item v-if="form.promiseFile" label="通知书/承诺函">
              <a :href="$toPreview(form.promiseFile.fileUrl)" target="_blank">{{ form.promiseFile.fileName }}</a>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item v-if="form.outsideFileList.length" label="非标清单附件">
              <ul>
                <li v-for="(file, index) in form.outsideFileList" :key="index">
                  <a :href="$toPreview(file.fileUrl)" target="_blank">{{ file.fileName }}</a>
                </li>
              </ul>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </el-card>

    <notice-base-form v-if="form.bussType === LEVEL_ONE_APPEAL.CP_NOTICE.code" ref="notice-base-form" class="m-t-lg" :base-params.sync="form.complaintClaim"></notice-base-form>

    <el-card v-if="!isInit" class="m-t-md" :header="`歌单信息${ cantEdit ? '' : '录入' }`">
      <tort-complaint-add-table
        v-if="form.bussType === LEVEL_ONE_APPEAL.TORT.code"
        :data="showTrackList"
        :disabled="cantEdit"
        @upload="handleUploadSongList"
        @save="handleSaveSongList"
      >
      </tort-complaint-add-table>
      <offline-add-table
        v-else-if="form.bussType === LEVEL_ONE_APPEAL.OFFLINE.code"
        :data="showTrackList"
        :disabled="cantEdit"
        @upload="handleUploadSongList"
        @save="handleSaveSongList"
      >
      </offline-add-table>
      <white-list-add-table
        v-else-if="form.bussType === LEVEL_ONE_APPEAL.WHITE_LIST.code"
        :data="showTrackList"
        :disabled="cantEdit"
        @upload="handleUploadSongList"
        @save="handleSaveSongList"
      ></white-list-add-table>
      <notice-add-table
        v-else
        :data="showTrackList"
        :disabled="cantEdit"
        @upload="handleUploadSongList"
        @save="handleSaveSongList"
      ></notice-add-table>
    </el-card>

    <section class="btn-group">
      <el-button v-if="canSubmit" @click="handleSaveDraft">保存草稿</el-button>
      <el-button v-if="!isInit && (canSubmit || canCheck)" type="primary" @click="handleSubmit">提交</el-button>
    </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { Complaint, CpNotice } from '@/models/Complaint'
import complaintService from '@/api/complaint'
import TortComplaintAddTable from './components/AddTable/TortComplaintAddTable'
import OfflineAddTable from './components/AddTable/OfflineAddTable'
import WhiteListAddTable from './components/AddTable/WhiteListAddTable'
import NoticeAddTable from './components/AddTable/NoticeAddTable.vue'
import NoticeBaseForm from '@/views/complaint/outside/cpTort/components/NoticeBaseForm.vue'
import {
  APPEAL_STATUS,
  LEVEL_ONE_APPEAL,

  getLevelOneMap,
  getLevelTwoMap,
  highTypeMap,
  appealFileLevelMap,
  appealUserTypeMap,
  mediaTypeMap,
  appealPlatMap,
  appealFromMap,
  appealTypeMap,
} from './const'

export default {
  name: 'ComplaintEdit',
  components: {
    TortComplaintAddTable,
    OfflineAddTable,
    WhiteListAddTable,
    NoticeAddTable,
    NoticeBaseForm,
  },
  data() {
    return {
      LEVEL_ONE_APPEAL,

      highTypeMap,
      appealUserTypeMap,
      mediaTypeMap,
      appealPlatMap,
      appealFileLevelMap,
      appealFromMap,
      appealTypeMap,

      form: new Complaint(),
      rules: {
        appealName: [{ required: true, message: '请输入申诉方', trigger: 'blur' }],
        complaintType: [{ required: true, message: '请选择侵权类型', trigger: 'change' }],
        appealFrom: [{ required: true, message: '请选择申诉来源', trigger: 'change' }]
      },
      loading: false,
      quickSaving: false,
    }
  },

  computed: {
    ...mapGetters(['isAdmin', 'isEditor', 'isLegal', 'userInfo']),
    /**
     * @returns {String}
     */
    tip() {
      return '填写申诉单概况后，可点击右上角进行快速提单，或者先保存草稿上传歌单信息'
    },
    /**
     * @returns {Boolean}
     */
    isInit() {
      return !this.form.id
    },
    /**
     * @returns {Boolean}
     */
    isDraft() {
      return this.form.reviewStatus === 0
    },
    /**
     * @returns {Array}
     */
    showTrackList() {
      return this.form.complaintTrackList.filter(v => !v.isDel)
    },
    /**
     * @returns {Boolean}
     */
    isSelf() {
      return this.form.createUser === this.userInfo.openId
    },
    /**
     * 能修改一些基础信息
     */
    canSubmit() {
      return this.isInit || this.isSelf || this.isAdmin
    },
    /**
     * 不能编辑申诉单
     * 1. 非本人非初始化且非管理员
     * 2. 审核中
     * @returns {Boolean}
     */
    cantEdit() {
      return !this.canSubmit ||
      [
        APPEAL_STATUS.DEALING.code,
        APPEAL_STATUS.ACCEPTANCE.code,
        APPEAL_STATUS.FINISHED.code
      ].includes(this.form.reviewStatus)
    },
    /**
     * 能否审核
     * 1. 编务、法务、管理员
     * 2. 单据状态为未审核
     * @returns {Boolean}
     */
    canCheck() {
      return (this.isAdmin || this.isEditor || this.isLegal) && !this.isInit
    },
    levelOneAppealMap() {
      return getLevelOneMap(this.form.applicationType)
    },
    levelTwoAppealMap() {
      return getLevelTwoMap(this.form.applicationType, this.form.bussType)
    }
  },

  watch: {
    'form.bussType'(val) {
      if (val === LEVEL_ONE_APPEAL.CP_NOTICE.code && !this.form.complaintClaim) {
        this.form.complaintClaim = new CpNotice()
      } else if (val !== LEVEL_ONE_APPEAL.CP_NOTICE.code) {
        this.form.complaintClaim = null
      }
    }
  },

  created() {
    const id = this.$route.query.id
    if (id) {
      this.fetchData(id)
    }
  },

  methods: {
    handleApplicationTypeChange(val) {
      this.form.bussType = +Object.keys(this.levelOneAppealMap)[0]
      this.handleBussTypeChange(this.form.bussType)
    },
    handleBussTypeChange(val) {
      this.form.complaintType = +Object.keys(this.levelTwoAppealMap)[0]
    },
    // 外部用户不允许提白名单类型申诉单
    disableBussType(type) {
      if (type === LEVEL_ONE_APPEAL.WHITE_LIST.code && [0, 1].includes(this.form.appealType)) return true
      return false
    },
    async fetchData(id) {
      this.loading = true
      try {
        const data = await complaintService.findById(id)
        this.form = new Complaint(data)
      } catch (error) {
        this.$alert(`查询失败.${error.message}`, '错误', {
          type: 'error'
        })
      }
      this.loading = false
    },
    async handleQuickSave() {
      try {
        await this.$refs.form.validate()
      } catch (e) {
        return
      }

      this.quickSaving = true
      try {
        const { id } = await complaintService.save(this.form.getBackendData())
        this.$message.success('提单成功，即将跳转')
        setTimeout(() => {
          this.$router.push({ name: 'appealList', query: { id } })
        }, 1000)
      } catch (e) {
        this.$alert(`保存失败.${e.message}`, '错误', {
          type: 'error'
        })
      }
      this.quickSaving = false
    },

    async handleUploadSongList(params) {
      try {
        await complaintService.batchUpload({
          ...params,
          id: this.form.id
        })
        this.handleSaveDraft()
      } catch (error) {
        this.$message.error(error.message)
      }
    },
    async handleSaveSongList(data) {
      const idx = this.form.complaintTrackList.findIndex(v => v.tracklistId === data.tracklistId)
      if (idx > -1) {
        this.form.complaintTrackList.splice(idx, 1, data)
      } else {
        this.form.complaintTrackList.push(data)
      }

      this.handleSaveDraft()
    },

    async save(cb) {
      const postData = {
        ...this.form.getBackendData()
      }
      try {
        const { id } = await complaintService.save(postData)
        this.$message.success('保存成功！')
        cb(id)
      } catch (error) {
        this.$message.error(error.message)
      }
    },

    async handleSaveDraft() {
      try {
        await this.$refs.form.validate()
      } catch (error) {
        const firstErr = error[Object.keys(error)[0]][0]
        return this.$message.error(firstErr.message)
      }

      const cb = (id) => {
        this.$router.replace({ query: { id } }).catch(error => {
          // 屏蔽同页面跳转报错
          if (error.name !== 'NavigationDuplicated') {
            throw error
          }
        })
        this.fetchData(id)
      }
      this.save(cb)
    },
    async handleSubmit() {
      try {
        await this.$refs.form.validate()
      } catch (error) {
        const firstErr = error[Object.keys(error)[0]][0]
        return this.$message.error(firstErr.message)
      }

      if (!this.form.complaintTrackList.length) {
        return this.$message.error('请至少上传一行歌单')
      }

      const cb = (id) => {
        this.$router.push({ name: 'appealList', query: { id } })
      }
      // isFinal = 1 标识用户最终提交
      this.form.isFinal = 1
      this.save(cb)
    }
  }
}
</script>

<style lang="scss" scoped>
.task-card-header {
  padding-top: 4px;
  padding-bottom: 12px;
}

.task-tip {
  margin-bottom: 12px;
  color: #1572FB;
  background: rgba(235, 241, 255, 0.5);
  border: 1px solid #7DA7FF;
  border-radius: 2px;
}
.form-field {
  width: 212px;
}

.btn-group {
  margin-top: 24px;
  text-align: center;
}

::v-deep {
  .file-uploader {
    border-radius: 4px;
    padding: 10px 0;
    margin-top: 24px;
    text-align: center;

    .el-upload-list {
      text-align: left;
    }

    .el-upload.el-upload--text {
      width: 100%;
      background: #FAFBFB;
      border: 1px dashed #4071FF;
      border-radius: 4px;
      padding: 16px;
    }

    .el-upload-dragger {
      background-color: unset;
      border: none;
      width: 100%;
      min-height: 120px;
      height: 100%;
    }
  }

  .el-collapse {
    margin-top: 16px;
    border-top: none;
    border-bottom: none;

    .el-collapse-item__header {
      flex-direction: row-reverse;
      justify-content: flex-end;
      padding-left: 10px;
      border-bottom: none;
      background: #F5F8FF;
      border-radius: 4px;
    }

    .el-collapse-item__content {
      padding: 0 20px 20px;
      background: #F5F8FF;
    }

    .el-collapse-item__arrow {
      margin: 0;
      color: #1572FB;
      font-size: 24px;
    }
  }
}

</style>
